import {
  TokenResponseSchema,
  MeResponseSchema,
  OAuthLinkInitResponseSchema,
  type RegisterEmpresaInput,
  type RegisterProfissionalInput,
  type LoginInput,
  type ForgotPasswordInput,
  type ResetPasswordInput,
  type TokenResponse,
  type MeResponse,
  type CompleteSignupInput,
  type LinkProviderInput,
  type AppleNativeAuthInput,
  type AppleNativeCallbackInput,
  type OAuthLinkInitInput,
  type OAuthLinkInitResponse,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Typed wrapper over the auth REST surface. Constructed once per ApiClient instance
 * and exposed as `apiClient.auth.*`. All inputs use Zod-derived types from
 * `@lance-x/shared`, the same schemas backend uses for validation.
 */
export class AuthEndpoints {
  constructor(private readonly client: ApiClient) {}

  registerEmpresa(input: RegisterEmpresaInput): Promise<TokenResponse> {
    return this.client.post('/auth/register/empresa', input, {
      responseSchema: TokenResponseSchema,
    });
  }

  registerProfissional(input: RegisterProfissionalInput): Promise<TokenResponse> {
    return this.client.post('/auth/register/profissional', input, {
      responseSchema: TokenResponseSchema,
    });
  }

  login(input: LoginInput): Promise<TokenResponse> {
    return this.client.post('/auth/login', input, {
      responseSchema: TokenResponseSchema,
    });
  }

  /** PWA: relies on the lance.refresh HttpOnly cookie. Native: sends body.refreshToken. */
  refresh(refreshToken?: string): Promise<TokenResponse> {
    return this.client.post(
      '/auth/refresh',
      refreshToken ? { refreshToken } : {},
      { responseSchema: TokenResponseSchema },
    );
  }

  logout(): Promise<{ message: string }> {
    return this.client.post('/auth/logout');
  }

  forgotPassword(input: ForgotPasswordInput): Promise<{ message: string }> {
    return this.client.post('/auth/forgot-password', input);
  }

  resetPassword(input: ResetPasswordInput): Promise<{ message: string }> {
    return this.client.post('/auth/reset-password', input);
  }

  me(): Promise<MeResponse> {
    return this.client.get('/auth/me', { responseSchema: MeResponseSchema });
  }

  // ============================================================
  // PHASE 16 v1.3 — SOCIAL-01/02: Google + Apple OAuth + complete-signup
  // ============================================================

  /**
   * Absolute URL to kick off Google OAuth. The mobile app does
   * `window.location.href = client.auth.googleAuthUrl()` (PWA) or opens an
   * in-app browser pointing at this URL (Capacitor). Backend redirects to
   * Google → callback → mobile deep-link with `?token` + `?isNew`.
   */
  googleAuthUrl(): string {
    return `${this.client.baseUrl}/auth/google`;
  }

  /** Absolute URL to kick off Sign in with Apple. PWA-Safari fallback target. */
  appleAuthUrl(): string {
    return `${this.client.baseUrl}/auth/apple`;
  }

  /**
   * Finalize OAuth signup — call with the JWT issued by the OAuth callback
   * (set via setAccessToken on the client). Discriminator branch chosen at
   * runtime (`role: 'empresa' | 'profissional'`). Returns the refreshed
   * TokenResponse so the caller can replace the cached access token.
   */
  completeSignup(input: CompleteSignupInput): Promise<TokenResponse> {
    return this.client.post('/auth/complete-signup', input, {
      responseSchema: TokenResponseSchema,
    });
  }

  // ============================================================
  // PHASE 20 v1.4 — LINK-01 / APPLE-01
  // ============================================================

  /**
   * Attach a new OAuth identity (Google or Apple) to the currently
   * authenticated user. Returns 204 No Content; throws ApiError(409,
   * 'oauth_identity_already_linked') when the identity is already linked
   * elsewhere. Idempotent for same-user re-link.
   */
  linkProvider(input: LinkProviderInput): Promise<void> {
    return this.client.post('/auth/link-provider', input);
  }

  /**
   * Native iOS Sign in with Apple callback. Sent by Capacitor
   * `@capacitor-community/apple-sign-in` after authorize completes on device.
   * Server verifies the idToken via Apple JWK and upserts the User. Response
   * mirrors web TokenResponse + an extra `isNew` flag the mobile app uses to
   * decide between routing to `/complete-cadastro` (true) or `/home` (false).
   */
  appleNativeCallback(
    input: AppleNativeCallbackInput,
  ): Promise<TokenResponse & { isNew: boolean }> {
    return this.client.post<TokenResponse & { isNew: boolean }>(
      '/auth/apple/native-callback',
      input,
    );
  }

  /**
   * Phase 46 v1.11 — APPLE-NATIVE-01. Spec-aligned successor to
   * `appleNativeCallback`. Posts the Apple-plugin response payload to the new
   * `/auth/apple/native` endpoint, using the `identityToken` field name that
   * matches the plugin / iOS `ASAuthorizationAppleIDCredential` shape.
   */
  appleNative(
    input: AppleNativeAuthInput,
  ): Promise<TokenResponse & { isNew: boolean }> {
    return this.client.post<TokenResponse & { isNew: boolean }>(
      '/auth/apple/native',
      input,
    );
  }

  // ============================================================
  // PHASE 21 v1.4 — LINK-01 web closure
  // ============================================================

  /**
   * Initiate the OAuth web link flow. Server mints a 5-minute state JWT
   * bound to the currently-authenticated user and returns the absolute
   * `/auth/{provider}?state=<jwt>` URL the caller redirects to via
   * `window.location.href = response.url`. After Google/Apple complete,
   * the backend calls `OAuthService.linkProvider` (instead of upsertFromX)
   * and redirects to `${MOBILE_BASE_URL}/auth/oauth-success?linked={true|conflict|error}&provider={google|apple}`.
   */
  oauthLinkInit(input: OAuthLinkInitInput): Promise<OAuthLinkInitResponse> {
    return this.client.post('/auth/oauth/link-init', input, {
      responseSchema: OAuthLinkInitResponseSchema,
    });
  }
}
